/**
 * A HOC that renders a ModuleList using modules in the logged-out-homepage
 * package.
 */

import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import {compose} from "mu-lambda";
import * as React from "react";

import {
    RoughCircledBox,
    RoughHighlight,
    RoughUnderline,
} from "@khanacademy/khannotations";
import {useOnMountEffect, View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    enums,
    withConversionContext,
    withMarkConversion,
    type WithMarkConversionProps,
    type WithoutMarkConversion,
} from "@khan/analytics-learning-time";
import {RequestInfo} from "@khan/shared-core";

import PageRoot from "#app-shell-package/page-root.tsx";
import {getKaid} from "#data-access/profile-utils.ts";
import withUserProfile, {
    type WithoutUserProfile,
    type WithUserProfileProps,
} from "#data-access/with-user-profile.tsx";
import getClassyCampaignURL from "#donate-package/classy.ts";
import {MarketingColors} from "#marketing-content-shared-package/constants.ts";
import InfoBlock from "#marketing-content-shared-package/info-block.tsx";
import List from "#marketing-content-shared-package/list.tsx";
import Testimonial from "#marketing-content-shared-package/testimonial.tsx";
import ModuleList from "#modules-package/module-list.tsx";
import type {Module} from "#modules-package/types.ts";
import {useNewRegistrationFlowFeature} from "#shared/auth/utils/use-new-registration-flow-feature.ts";

import {withStrings} from "./locales/logged-out-homepage-module-list/load-strings.tsx";
import {LohpRouterModule} from "./lohp-router.tsx";
import Media from "./media.tsx";
import CourseListContainer from "./modules/course-list-container.tsx";
import HeroCondensed from "./modules/hero-condensed.tsx";
import HeroKhanmigo from "./modules/hero-khanmigo.tsx";
import LastChance from "./modules/last-chance.tsx";
import Supporters from "./modules/supporters.tsx";

type StrutModuleProps = {
    kind: "StrutModule";
};

const StrutModule = ({kind}: StrutModuleProps): React.ReactElement => {
    return (
        <Strut
            size={spacing.xxxLarge_64}
            style={{height: `${spacing.xxxLarge_64}px`}}
        />
    );
};

// A collection of all possible LOHP modules
const loggedOutHomepageModules = {
    LohpRouterModule,
    HeroKhanmigo,
    HeroCondensed,
    CourseListContainer,
    List,
    InfoBlock,
    Testimonial,
    LastChance,
    Supporters,
    StrutModule,
} as const;

const showDistricts = () =>
    (RequestInfo.kaLocale === "en" || RequestInfo.kaLocale === "es") &&
    RequestInfo.remoteCountry === "US";

const getModules = (
    kaid?: string | null,
    newRegistrationExperiment?: boolean | null,
) => {
    const isEnUs =
        RequestInfo.kaLocale === "en" && RequestInfo.remoteCountry === "US";

    const khanmigoModule = isEnUs
        ? ({
              kind: "HeroKhanmigo",
          } as const)
        : ({
              kind: "HeroCondensed",
              showDistrictsCTA: showDistricts(),
          } as const);

    const newRegistrationHeroModule = {
        kind: "LohpRouterModule",
    } as const;

    const lohpCourseListModule = {
        kind: "CourseListContainer",
    } as const;

    const strutModule = {
        kind: "StrutModule",
    } as const;

    let trustedContentCopy = t`Created by experts, Khan Academy’s library of trusted practice and lessons covers math, science, and more. Always free for learners and teachers.`;

    if (RequestInfo.kaLocale === "en" && RequestInfo.remoteCountry === "US") {
        trustedContentCopy =
            "Created by experts, Khan Academy’s library of trusted, " +
            "standards-aligned practice and lessons covers math K-12 " +
            "through early college, grammar, science, history, AP®, " +
            "SAT®, and more. It’s all free for learners and " +
            "teachers.";
    } else if (
        RequestInfo.kaLocale === "en" &&
        RequestInfo.remoteCountry === "IN"
    ) {
        trustedContentCopy =
            "Created by experts, Khan Academy’s library of trusted, " +
            "standards-aligned practice and lessons covers math K-12 " +
            "through early college, grammar, science, history, SAT®, " +
            "and more. It’s all free for learners and teachers.";
    }

    const lohpWhyKaWorksModule = {
        kind: "List",
        headline: t`Why Khan Academy works`,
        listItems: [
            {
                kind: "ListItem",
                illustration: "PersonalizedLearningIcon",
                headline: t`Personalized learning`,
                copy: t`Students practice at their own pace, first filling in gaps in their understanding and then accelerating their learning.`,
            },
            {
                kind: "ListItem",
                illustration: "TrustedContentIcon",
                headline: t`Trusted content`,
                copy: trustedContentCopy,
            },
            {
                kind: "ListItem",
                illustration: "EmpowerTeachersIcon",
                headline: t`Tools to empower teachers`,
                copy: t`With Khan Academy, teachers can identify gaps in their students’ understanding, tailor instruction, and meet the needs of every student.`,
            },
        ],
        backgroundIllustration: null,
        scrollId: "why-ka-works",
    } as const;

    const lohpDistrictsModule = {
        kind: "InfoBlock",
        title: t`DISTRICTS`,
        headline:
            RequestInfo.kaLocale === "en" ? (
                <Trans>
                    Khan Academy boosts
                    <br />
                    <RoughHighlight
                        roughStyle={highlightStyle}
                        animation={speedAnimation}
                    >
                        student learning
                    </RoughHighlight>
                </Trans>
            ) : (
                <Trans>
                    Khan Academy boosts
                    <br />
                    <RoughCircledBox
                        delay={200}
                        roughStyle={circleStyle}
                        marginLeft={20}
                        marginRight={20}
                        marginTop={-10}
                        marginBottom={0}
                    >
                        student learning
                    </RoughCircledBox>
                </Trans>
            ),
        copy: (
            <Trans>
                In a{" "}
                <a
                    className={css(styles.link)}
                    href="https://www.prnewswire.com/news-releases/khan-academy-introduces-new-mastery-learning-features-300708027.html"
                >
                    recent study
                </a>
                , students were over twice as likely to meet grade-level
                standards with Khan Academy. Districts across the country are
                partnering with us to ensure all their students succeed.
            </Trans>
        ),
        cta: {
            copy: t`Districts, start here`,
            type: "button",
            url: "https://districts.khanacademy.org/",
            conversion: "logged_out_districts_module_cta_clicked",
        },
        staticGraphic: "/images/lohp/graph_collage@2x.png",
        align: "left",
    } as const;

    let teacherModuleHeadline: string | React.ReactElement =
        t`Differentiate your classroom and engage every student.`;
    let teacherModuleAttribution = null;
    let teacherModuleHangQuote = false;

    const teacherModuleHeadlineQuote =
        RequestInfo.kaLocale === "en" ? (
            <Trans>
                “I’m finally able to truly{" "}
                <RoughUnderline
                    animation={speedAnimationHero1}
                    roughStyle={underlineStyleHero1}
                >
                    differentiate
                </RoughUnderline>{" "}
                my classroom. This has been priceless for my students’
                engagement.”
            </Trans>
        ) : (
            <Trans>
                “I’m finally able to truly{" "}
                <RoughHighlight
                    roughStyle={highlightStyle}
                    animation={speedAnimation}
                >
                    differentiate
                </RoughHighlight>{" "}
                my classroom. This has been priceless for my students’
                engagement.”
            </Trans>
        );

    if (RequestInfo.kaLocale === "en" && RequestInfo.remoteCountry === "US") {
        teacherModuleHeadline = teacherModuleHeadlineQuote;
        // @ts-expect-error - TS2322 - Type '"DUSTIN FAUTH / 4th grade teacher / Falmouth, MA"' is not assignable to type 'null'.
        teacherModuleAttribution =
            "DUSTIN FAUTH / 4th grade teacher / Falmouth, MA";
        teacherModuleHangQuote = true;
    } else if (
        RequestInfo.kaLocale === "en" &&
        RequestInfo.remoteCountry === "IN"
    ) {
        teacherModuleHeadline = teacherModuleHeadlineQuote;
        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null'.
        teacherModuleAttribution =
            "UDAYA LAKSHMI PALAPALA / Middle school Coordinator / " +
            "Hyderabad, Telangana";
        teacherModuleHangQuote = true;
    } else if (
        RequestInfo.kaLocale === "es" &&
        RequestInfo.remoteCountry === "PE"
    ) {
        teacherModuleHeadline = teacherModuleHeadlineQuote;
        // @ts-expect-error - TS2322 - Type '"RAUL CAROY / Maestro IE Nuestra Señora de Guadalupe / Lima"' is not assignable to type 'null'.
        teacherModuleAttribution =
            "RAUL CAROY / Maestro IE Nuestra Señora de Guadalupe / Lima";
        teacherModuleHangQuote = true;
    } else if (
        RequestInfo.kaLocale === "es" &&
        RequestInfo.remoteCountry === "MX"
    ) {
        teacherModuleHeadline = teacherModuleHeadlineQuote;
        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null'.
        teacherModuleAttribution =
            "ADRIANA FLORES / Bachillerato, 1er semestre / Ciudad de " +
            "México";
        teacherModuleHangQuote = true;
    } else if (
        RequestInfo.kaLocale === "pt" &&
        RequestInfo.remoteCountry === "BR"
    ) {
        teacherModuleHeadline = teacherModuleHeadlineQuote;
        // @ts-expect-error - TS2322 - Type '"RENATO AUGUSTO / Diretor do Colégio Embraer / Botucatu/SP"' is not assignable to type 'null'.
        teacherModuleAttribution =
            "RENATO AUGUSTO / Diretor do Colégio Embraer / Botucatu/SP";
        teacherModuleHangQuote = true;
    }

    const ctaUrl = "/signup?isteacher=1";

    const lohpTeachersModule = {
        kind: "InfoBlock",
        title: t`TEACHERS`,
        headline: teacherModuleHeadline,
        attribution: teacherModuleAttribution,
        copy: t`We empower teachers to support their entire classroom. 90% of US teachers who have used Khan Academy have found us effective.`,
        cta: {
            copy: t`Teachers, start here`,
            type: "button",
            url: ctaUrl,
            conversion: "logged_out_teachers_module_cta_clicked",
        },
        staticGraphic: "/images/lohp/faces_collage_2@2x.png",
        align: "right",
        hangQuote: teacherModuleHangQuote,
    } as const;

    let learnersModulecopy = t`Build a deep, solid understanding in math, science, and more.`;

    if (RequestInfo.kaLocale === "en" && RequestInfo.remoteCountry === "US") {
        learnersModulecopy =
            "Build a deep, rock-solid understanding in math, grammar, " +
            "science, history, SAT®, AP®, and more.";
    } else if (
        RequestInfo.kaLocale === "en" &&
        RequestInfo.remoteCountry === "IN"
    ) {
        learnersModulecopy =
            "Build a deep, solid understanding in math, science, grammar, " +
            "history, SAT®, and more.";
    }

    const lohpLearnersModule = {
        kind: "InfoBlock",
        title: t`LEARNERS AND STUDENTS`,
        headline:
            RequestInfo.kaLocale === "en" ? (
                <Trans>
                    <RoughHighlight
                        roughStyle={highlightStyle}
                        animation={speedAnimation}
                    >
                        You
                    </RoughHighlight>{" "}
                    can learn anything.
                </Trans>
            ) : (
                <Trans>
                    <RoughUnderline
                        animation={speedAnimationHero1}
                        roughStyle={underlineStyleHero1}
                    >
                        <RoughUnderline
                            animation={speedAnimationHero2}
                            roughStyle={underlineStyleHero2}
                        >
                            You
                        </RoughUnderline>
                    </RoughUnderline>{" "}
                    can learn anything.
                </Trans>
            ),
        copy: learnersModulecopy,
        cta: {
            copy: t`Learners, start here`,
            type: "button",
            url: "/signup",
            conversion: "logged_out_learners_module_cta_clicked",
        },
        staticGraphic: "/images/lohp/laptop_collage@2x.png",
        staticGraphicEasterEgg: {
            id: "easterEggMap",
            coords: {
                top: "51%",
                right: "46.5%",
                bottom: "43%",
                left: "46%",
            },
            buttonText: t`Click for a surprise`,
            ariaMessage: t`Surprise Confetti!`,
        },
        align: "left",
    } as const;

    let testimonialModuleQuote = t`“I come from a poor family. At home it’s one room, just a room we live in. When I was a child, I used to fear mathematics. But now, I am in love with mathematics because of Khan Academy.”`;
    let testimonialMeta = t`India`;

    if (RequestInfo.kaLocale === "en" && RequestInfo.remoteCountry === "IN") {
        testimonialModuleQuote =
            "“When I was a child, I used to fear mathematics. But now, I " +
            "am in love with mathematics because of Khan Academy.”";
        testimonialMeta = "Gurugram, Haryana";
    }

    const lohpTestimonialModule = {
        kind: "Testimonial",
        quote: testimonialModuleQuote,
        attribution: t`ANJALI`,
        meta: testimonialMeta,
        staticGraphic: "/images/lohp/anjali-3.png",
        backgroundIllustration: "BackgroundBlob",
    } as const;

    let donorsModuleCopy = null;

    if (RequestInfo.kaLocale === "en") {
        // TODO(joshuan): Sometime after August 2019, show this copy in all
        // locales.
        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null'.
        donorsModuleCopy = t`Across the globe, 617 million children are missing basic math and reading skills. We’re a nonprofit delivering the education they need, and we need your help. You can change the course of a child’s life.`;
    } else {
        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'null'.
        donorsModuleCopy = t`Across the globe, 617 million children are missing basic math and reading skills. You can change the course of a child’s life.`;
    }

    const lohpDonorsModule = {
        kind: "InfoBlock",
        title: t`TOGETHER WE CAN MAKE A DIFFERENCE`,
        headline: (
            <Trans>
                <RoughUnderline
                    animation={speedAnimationHero1}
                    roughStyle={underlineStyleHero1}
                >
                    <RoughUnderline
                        animation={speedAnimationHero2}
                        roughStyle={underlineStyleHero2}
                    >
                        Every child
                    </RoughUnderline>
                </RoughUnderline>{" "}
                deserves the chance to learn.
            </Trans>
        ),
        copy: donorsModuleCopy,
        cta: {
            copy: t`Give them the chance`,
            type: "button",
            url: getClassyCampaignURL(RequestInfo.kaLocale, {
                c_src: kaid,
                c_src2: "Webapp.lohpdonormodule",
            }),
            newWindow: true,
            conversion: "logged_out_donors_module_cta_clicked",
        },
        align: "right",
        staticGraphic: "/images/lohp/math-unicorn-donate-collage.png",
    } as const;

    const lohpLastChanceModule = {
        kind: "LastChance",
        showDistrictsCTA: showDistricts(),
        localeIsEnglish: RequestInfo.kaLocale === "en",
    } as const;

    const lohpSupportersModule = {
        kind: "Supporters",
        backgroundIllustration: "SupportersBackground",
    } as const;

    const modules: Array<Module> = [];

    if (newRegistrationExperiment) {
        modules.push(newRegistrationHeroModule);
    } else {
        modules.push(khanmigoModule);
    }

    modules.push(lohpCourseListModule);

    if (newRegistrationExperiment && isEnUs) {
        // Since the new registration is placed at the top, the Khanmigo Module
        // is moved further down the list.
        modules.push(strutModule);
        modules.push(khanmigoModule);
    }

    modules.push(lohpWhyKaWorksModule);
    if (showDistricts()) {
        modules.push(lohpDistrictsModule);
    }
    modules.push(lohpTeachersModule);
    modules.push(lohpLearnersModule);
    modules.push(lohpTestimonialModule);
    modules.push(lohpDonorsModule);
    modules.push(lohpLastChanceModule);
    modules.push(lohpSupportersModule);

    return modules;
};

type Props = WithUserProfileProps & WithMarkConversionProps;

const LoggedOutHomePageModuleList: React.ComponentType<Props> = ({
    profile,
    markConversion,
}) => {
    const inExperiment = useNewRegistrationFlowFeature();
    const modules = getModules(getKaid(profile), inExperiment);
    const filteredModules = modules.filter((module) => {
        return loggedOutHomepageModules.hasOwnProperty(module.kind);
    });

    useOnMountEffect(() => {
        markConversion({id: "logged_out_homepage_visit"});
    });

    return (
        <PageRoot
            settings={{
                fullTitle: `${t`Khan Academy`} | ${t`Free Online Courses, Lessons & Practice`}`,
                description: t`Learn for free about math, art, computer programming, economics, physics, chemistry, biology, medicine, finance, history, and more. Khan Academy is a nonprofit with the mission of providing a free, world-class education for anyone, anywhere.`,
            }}
        >
            <View style={styles.lohp}>
                <Media>
                    <ModuleList
                        domain="default"
                        modules={filteredModules}
                        moduleComponents={loggedOutHomepageModules}
                    />
                </Media>
            </View>
        </PageRoot>
    );
};

const circleStyle = {
    roughness: 2,
    stroke: MarketingColors.highlightTeal,
    strokeWidth: 2,
} as const;

const highlightStyle = {
    roughness: 2,
    fill: MarketingColors.highlightTeal,
} as const;

const speedAnimation = {
    animation: "speed",
    speed: 5,
    delay: 200,
} as const;

const speedAnimationHero1 = {
    animation: "speed",
    speed: 10,
    delay: 1200,
} as const;

const speedAnimationHero2 = {
    animation: "speed",
    speed: 10,
    delay: 20,
} as const;

const underlineStyleHero1 = {
    roughness: 0,
    stroke: MarketingColors.highlightTeal,
    strokeWidth: 2,
    bowing: {
        bowing: "inverse",
        ratio: 100,
    },
    lift: 4,
} as const;

const underlineStyleHero2 = {
    roughness: 5,
    stroke: MarketingColors.highlightTeal,
    strokeWidth: 2,
    bowing: {
        bowing: "inverse",
        ratio: 100,
    },
    lift: 6,
} as const;

const styles = StyleSheet.create({
    lohp: {
        // The blobs and collages intentionally render beyond the width of most displays.
        // Prevent that from expanding the screen width.
        overflowX: "hidden",
    },
    link: {
        color: "#1865F2",
        fontWeight: "bold",
        textDecoration: "none",
        ":hover": {
            textDecoration: "underline",
        },
    },
});

type ExportProps = WithoutMarkConversion<
    WithoutUserProfile<
        JSX.LibraryManagedAttributes<
            typeof LoggedOutHomePageModuleList,
            React.ComponentProps<typeof LoggedOutHomePageModuleList>
        >
    >
>;
const LoggedOutHomePageModuleListWithLearningTime: React.ComponentType<ExportProps> =
    compose(
        withConversionContext({
            activity: enums.Activity.NAVIGATING,
        }),
        withMarkConversion,
        withUserProfile,
    )(LoggedOutHomePageModuleList);
export default withStrings(LoggedOutHomePageModuleListWithLearningTime);
